import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { SITE } from "../config/site";
import { SERVICES } from "../data/services";

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-300 ${
    isActive ? "text-white" : "text-slate-400 hover:text-white"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-night/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img
            src={SITE.logo}
            alt="SparkGrid Solutions logo"
            className="h-10 w-10 rounded-lg object-cover object-[50%_18%] ring-1 ring-white/15"
          />
          <span className="font-syne text-lg font-bold tracking-tight text-white">
            Spark
            <span className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">
              Grid
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <NavLink to="/" end data-testid="nav-link-home" className={linkCls}>
            Home
          </NavLink>
          <div className="relative" onMouseEnter={() => setDrop(true)} onMouseLeave={() => setDrop(false)}>
            <button
              data-testid="nav-link-services"
              onClick={() => setDrop(!drop)}
              className="flex items-center gap-1 text-sm font-medium text-slate-400 transition-colors hover:text-white"
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${drop ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {drop && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-1/2 top-full w-80 -translate-x-1/2 pt-4"
                >
                  <div className="rounded-2xl border border-white/10 bg-night-2 p-2 shadow-2xl shadow-black/60">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        onClick={() => setDrop(false)}
                        className="group flex items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-white/5"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">{s.name}</span>
                          <span className="block text-xs text-slate-500">{s.tagline}</span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-spark" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/about" data-testid="nav-link-about" className={linkCls}>
            About Us
          </NavLink>
          <NavLink to="/contact" data-testid="nav-link-contact" className={linkCls}>
            Contact Us
          </NavLink>
          <Link
            to="/contact"
            data-testid="nav-cta-btn"
            className="rounded-full bg-gradient-to-r from-spark to-ember px-5 py-2.5 text-sm font-semibold text-night transition-transform duration-300 hover:scale-105"
          >
            Start a Project
          </Link>
        </nav>

        <button
          className="text-white lg:hidden"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-night lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              <NavLink to="/" end data-testid="nav-mobile-home" className={linkCls} onClick={() => setOpen(false)}>
                <span className="block rounded-lg px-3 py-2.5">Home</span>
              </NavLink>
              <p className="px-3 pt-3 font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Services</p>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`nav-mobile-service-${s.slug}`}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white"
                >
                  {s.name}
                </Link>
              ))}
              <NavLink to="/about" data-testid="nav-mobile-about" className={linkCls} onClick={() => setOpen(false)}>
                <span className="block rounded-lg px-3 py-2.5">About Us</span>
              </NavLink>
              <NavLink to="/contact" data-testid="nav-mobile-contact" className={linkCls} onClick={() => setOpen(false)}>
                <span className="block rounded-lg px-3 py-2.5">Contact Us</span>
              </NavLink>
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                onClick={() => setOpen(false)}
                className="mt-3 block rounded-full bg-gradient-to-r from-spark to-ember px-5 py-3 text-center text-sm font-semibold text-night"
              >
                Start a Project
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
